import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX, FiUserCheck, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { useToast } from './ToastContext';
import './../Styles/EditEmployeeDialog.css';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatRole = (role) => {
    if (!role) return 'Employee';
    const r = String(role).toLowerCase().trim();
    if (r === 'employee') return 'Employee';
    if (r === 'lead') return 'Project Lead';
    if (r === 'admin') return 'Manager / Admin';
    if (r === 'manager') return 'Manager';
    if (r === 'main_manager' || r === 'mainmanager') return 'Main Manager';
    return role.charAt(0).toUpperCase() + role.slice(1);
};

const EditEmployeeDialog = ({ employee, projects = [], onClose, onSave }) => {
    const showToast = useToast();
    const [formData, setFormData] = useState({
        employee_number: '',
        full_name: '',
        job_title: '',
        department: '',
        location: '',
        username: '',
        role: 'employee',
        project_name: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (employee) {
            setFormData({
                employee_number: employee.employee_number || employee.employee_id || '',
                full_name: employee.full_name || employee.name || '',
                job_title: employee.job_title || '',
                department: employee.department || '',
                location: employee.location || '',
                username: employee.username || employee.email || '',
                role: employee.role || 'employee',
                project_name: employee.project_name || employee.project || ''
            });
        }
    }, [employee]);

    // Handle Escape key to close dialog
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !isSaving) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, isSaving]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setError('');
        setSuccess('');
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.full_name.trim()) {
            setError('Full Name is required.');
            return;
        }

        if (formData.username && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username.trim())) {
            setError('Please enter a valid email address.');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                employee_number: formData.employee_number,
                employee_id: formData.employee_number,
                full_name: formData.full_name.trim(),
                job_title: formData.job_title.trim(),
                department: formData.department.trim(),
                location: formData.location.trim(),
                username: formData.username.trim(),
                email: formData.username.trim(),
                role: formData.role,
                project_name: formData.project_name.trim(),
                project: formData.project_name.trim()
            };

            const response = await axios.post(`${apiUrl}/api/update-user`, payload);

            if (response.status === 200) {
                if (showToast) {
                    showToast('Employee details updated successfully!', 'success');
                }
                setSuccess('Employee details updated successfully!');
                if (onSave) {
                    onSave(payload);
                }
                setTimeout(() => {
                    onClose();
                    window.location.reload();
                }, 500);
            } else {
                setError(response.data?.message || 'Failed to update employee details.');
            }
        } catch (err) {
            console.error('Error updating employee:', err);
            const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to update employee details. Please try again.';
            setError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    // Extract unique project options
    const projectOptions = Array.from(new Set([
        ...projects.map(p => typeof p === 'string' ? p : p.project_name || p.name).filter(Boolean),
        ...(formData.project_name ? [formData.project_name] : [])
    ])).filter(p => p !== 'None' && p !== 'All');

    return (
        <div className="edit-employee-dialog-overlay">
            <div className="edit-employee-dialog-content">
                {/* Header */}
                <div className="edit-employee-dialog-header">
                    <div className="edit-employee-header-info">
                        <div className="edit-employee-header-icon">
                            <FiUserCheck size={20} />
                        </div>
                        <div className="edit-employee-header-titles">
                            <h2>Edit Employee Details</h2>
                            <div className="edit-employee-emp-tag">
                                Employee ID: <span>{formData.employee_number || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="edit-employee-close-btn"
                        onClick={onClose}
                        disabled={isSaving}
                        title="Close"
                        aria-label="Close"
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* Form Body */}
                <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                    <div className="edit-employee-dialog-body">
                        {error && (
                            <div className="edit-employee-error-banner" role="alert">
                                <FiAlertCircle size={16} style={{ flexShrink: 0 }} />
                                <span>{error}</span>
                            </div>
                        )}

                        {success && (
                            <div className="edit-employee-success-banner" role="status">
                                <FiCheckCircle size={16} style={{ flexShrink: 0 }} />
                                <span>{success}</span>
                            </div>
                        )}

                        <div className="edit-employee-grid">
                            {/* Full Name */}
                            <div className="edit-form-field">
                                <label htmlFor="edit_full_name">
                                    Full Name <span className="required-star">*</span>
                                </label>
                                <input
                                    id="edit_full_name"
                                    type="text"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    placeholder="Enter full name"
                                    required
                                />
                            </div>

                            {/* Job Title */}
                            <div className="edit-form-field">
                                <label htmlFor="edit_job_title">Job Title</label>
                                <input
                                    id="edit_job_title"
                                    type="text"
                                    name="job_title"
                                    value={formData.job_title}
                                    onChange={handleChange}
                                    placeholder="e.g. Software Engineer"
                                />
                            </div>

                            {/* Department */}
                            <div className="edit-form-field">
                                <label htmlFor="edit_department">Department</label>
                                <input
                                    id="edit_department"
                                    type="text"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    placeholder="e.g. Engineering"
                                />
                            </div>

                            {/* Location */}
                            <div className="edit-form-field">
                                <label htmlFor="edit_location">Location</label>
                                <input
                                    id="edit_location"
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    placeholder="e.g. Hyderabad India"
                                />
                            </div>

                            {/* Email / Username */}
                            <div className="edit-form-field">
                                <label htmlFor="edit_username">Email Address</label>
                                <input
                                    id="edit_username"
                                    type="email"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder="employee@tsqsinc.com"
                                />
                            </div>

                            {/* Role (Non-editable, display only) */}
                            <div className="edit-form-field">
                                <label htmlFor="edit_role">Role</label>
                                <input
                                    id="edit_role"
                                    type="text"
                                    name="role"
                                    value={formatRole(formData.role)}
                                    disabled
                                    readOnly
                                    title="Role cannot be edited from this dialog"
                                    style={{
                                        cursor: 'not-allowed',
                                        backgroundColor: 'var(--bg-elevated, #f1f5f9)',
                                        color: 'var(--text-secondary, #475569)',
                                        fontWeight: '500'
                                    }}
                                />
                            </div>

                            {/* Project Assignment */}
                            <div className="edit-form-field full-width">
                                <label htmlFor="edit_project">Assigned Project</label>
                                <select
                                    id="edit_project"
                                    name="project_name"
                                    value={formData.project_name}
                                    onChange={handleChange}
                                >
                                    <option value="">None / Unassigned</option>
                                    {projectOptions.map((proj, idx) => (
                                        <option key={idx} value={proj}>
                                            {proj}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="edit-employee-dialog-footer">
                        <button
                            type="button"
                            className="btn-dialog-cancel"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-dialog-save"
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditEmployeeDialog;
